import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Scale, ShieldAlert, ArrowRight, Sparkles, Copy, Check } from "lucide-react";
import api from "@/lib/api";
import { useAuth, formatApiErrorDetail } from "@/contexts/AuthContext";
import PublicFooter from "@/components/PublicFooter";

const QUESTIONS = [
  {
    key: "tracking",
    label: "Request Tracking",
    weight: 1.0,
    q: "How does your agency currently track FOIA / public records requests?",
    options: [
      ["case_mgmt", "Dedicated case management software", 100],
      ["spreadsheet", "Email + spreadsheet", 50],
      ["shared_drive", "Shared drive / paper", 25],
      ["none", "No formal tracking", 0],
    ],
  },
  {
    key: "deadlines",
    label: "Deadline Compliance",
    weight: 1.2,
    q: "How often do you miss statutory response deadlines?",
    options: [
      ["never", "Never", 100],
      ["rarely", "Rarely (under 5%)", 75],
      ["sometimes", "Sometimes (10-25%)", 35],
      ["often", "Often (>25%)", 0],
    ],
  },
  {
    key: "redaction_review",
    label: "Redaction Review",
    weight: 1.0,
    q: "Are redactions reviewed by a second pair of eyes (legal/supervisor)?",
    options: [
      ["always", "Always", 100],
      ["usually", "Usually", 70],
      ["sometimes", "Sometimes", 35],
      ["never", "Never", 0],
    ],
  },
  {
    key: "audit_log",
    label: "Audit Trail",
    weight: 1.0,
    q: "Do you maintain a timestamped audit log of every action on every request?",
    options: [
      ["yes", "Yes \u2014 fully immutable", 100],
      ["partial", "Partial (notes only)", 45],
      ["no", "No", 0],
    ],
  },
  {
    key: "litigation",
    label: "Litigation History",
    weight: 1.3,
    q: "Have you been sued or threatened with FOIA litigation in the past 24 months?",
    options: [
      ["no", "No", 100],
      ["threatened", "Threatened only", 65],
      ["once", "Once", 35],
      ["multiple", "Multiple times", 0],
    ],
  },
];

export default function RiskAssessment() {
  const nav = useNavigate();
  const { checkAuth } = useAuth();
  const [answers, setAnswers] = useState({});
  const [done, setDone] = useState(false);

  // Trial-signup state
  const [signup, setSignup] = useState({ name: "", agency_name: "", email: "", website: "" });
  const [signupBusy, setSignupBusy] = useState(false);
  const [signupErr, setSignupErr] = useState("");
  const [tempPassword, setTempPassword] = useState(null);
  const [copied, setCopied] = useState(false);

  const score = (() => {
    if (Object.keys(answers).length === 0) return 0;
    const totalWeight = QUESTIONS.reduce((s, q) => s + q.weight, 0);
    const weightedSum = QUESTIONS.reduce((s, q, qi) => s + (answers[qi] ?? 0) * q.weight, 0);
    return Math.round(weightedSum / totalWeight);
  })();
  const categoryScores = QUESTIONS.map((q, qi) => ({
    key: q.key,
    label: q.label,
    value: answers[qi] ?? 0,
    answered: qi in answers,
  }));
  const grade =
    score >= 80
      ? { label: "Low Risk", tone: "success" }
      : score >= 60
      ? { label: "Moderate Risk", tone: "warning" }
      : score >= 40
      ? { label: "Elevated Risk", tone: "elevated" }
      : { label: "High Risk", tone: "danger" };
  const allAnswered = Object.keys(answers).length === QUESTIONS.length;

  const oneClickTrial = async (e) => {
    e.preventDefault();
    setSignupBusy(true);
    setSignupErr("");
    try {
      const { data } = await api.post("/auth/trial-from-assessment", {
        name: signup.name.trim(),
        email: signup.email.trim(),
        agency_name: signup.agency_name.trim(),
        risk_score: score,
        risk_grade: grade.label,
        risk_answers: answers,
        website: signup.website,
      });
      // Show password briefly so user can save it (the one-click experience never asked them to set one).
      if (data.temp_password) {
        setTempPassword(data.temp_password);
      } else {
        // Returning user: skip the password reveal, go straight to onboarding.
        await checkAuth();
        nav("/onboarding");
      }
    } catch (err) {
      setSignupErr(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSignupBusy(false);
    }
  };

  const enterWorkspace = async () => {
    await checkAuth();
    nav("/onboarding");
  };

  const copyPassword = async () => {
    try {
      await navigator.clipboard.writeText(tempPassword);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch (_) { /* clipboard unavailable — silently ignore */ }
  };

  return (
    <div className="min-h-screen bg-[#F8F9FA]">
      <header className="border-b border-slate-200 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between gap-3">
          <Link to="/" className="flex items-center gap-2 shrink-0">
            <Scale className="w-5 h-5 text-[#0B1B3D]" strokeWidth={1.75} />
            <span className="font-serif text-lg sm:text-xl">FOIA Assist</span>
          </Link>
          <div className="font-mono text-xs uppercase tracking-widest text-slate-500 text-right">Risk Assessment</div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto p-4 sm:p-6 lg:p-10">
        {!done ? (
          <>
            <div className="mb-6 sm:mb-8">
              <div className="font-mono text-xs uppercase tracking-widest text-slate-500 mb-2">15-minute audit</div>
              <h1 className="font-serif text-3xl sm:text-4xl tracking-tight font-medium mb-3">FOIA Risk Assessment</h1>
              <p className="text-slate-600 max-w-2xl">Five questions. Get an agency risk score and a personalized report. Used by attorneys to flag exposure.</p>
            </div>

            <div className="space-y-4">
              {QUESTIONS.map((q, qi) => (
                <div key={qi} className="fa-card p-5 sm:p-6">
                  <div className="text-xs uppercase tracking-widest text-slate-500 mb-2">Question {qi + 1} of {QUESTIONS.length}</div>
                  <div className="font-serif text-lg sm:text-xl mb-4">{q.q}</div>
                  <div className="space-y-2">
                    {q.options.map(([id, label, val]) => (
                      <button
                        key={id}
                        data-testid={`risk-q${qi}-${id}`}
                        onClick={() => setAnswers({ ...answers, [qi]: val })}
                        className={`w-full text-left p-3 border text-sm sm:text-base ${answers[qi] === val ? "border-[#0B1B3D] bg-[#0B1B3D]/5" : "border-slate-200 bg-white hover:border-slate-300"}`}
                      >
                        {label}
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            <button
              data-testid="risk-submit"
              disabled={!allAnswered}
              onClick={() => setDone(true)}
              className="fa-btn-primary mt-8 disabled:opacity-50 w-full sm:w-auto justify-center"
            >
              Calculate risk score <ArrowRight className="w-4 h-4" />
            </button>
          </>
        ) : (
          <div className="fa-card p-6 sm:p-10">
            <div className="flex items-center gap-3 mb-6 flex-wrap">
              <ShieldAlert className={`w-8 h-8 shrink-0 ${grade.tone === "danger" ? "text-red-600" : grade.tone === "elevated" ? "text-orange-600" : grade.tone === "warning" ? "text-amber-600" : "text-emerald-600"}`} strokeWidth={1.5} />
              <div>
                <div className="font-mono text-xs uppercase tracking-widest text-slate-500">Your Agency Risk Score</div>
                <div className="font-serif text-5xl sm:text-6xl mt-1" data-testid="risk-score-result">{score}<span className="text-slate-400 text-2xl sm:text-3xl"> / 100</span></div>
              </div>
            </div>
            <div className={`fa-badge fa-badge-${grade.tone}`} data-testid="risk-grade">{grade.label}</div>

            {/* Per-category breakdown — shows reviewer exactly which dimensions are driving the score */}
            <div className="mt-6 border-t border-slate-200 pt-6" data-testid="risk-category-breakdown">
              <div className="font-mono text-xs uppercase tracking-widest text-slate-500 mb-3">Score breakdown by category</div>
              <div className="space-y-2">
                {categoryScores.map((c) => {
                  const tone = c.value >= 80 ? "bg-emerald-500" : c.value >= 50 ? "bg-amber-500" : "bg-red-500";
                  return (
                    <div key={c.key} className="grid grid-cols-[1fr_auto] items-center gap-3" data-testid={`risk-cat-${c.key}`}>
                      <div>
                        <div className="text-sm text-slate-700 mb-1">{c.label}</div>
                        <div className="h-1.5 bg-slate-100 overflow-hidden">
                          <div className={`h-full ${tone} transition-all`} style={{ width: `${c.value}%` }} />
                        </div>
                      </div>
                      <div className="font-mono text-sm tabular-nums text-slate-700" data-testid={`risk-cat-${c.key}-value`}>{c.value}<span className="text-slate-400"> / 100</span></div>
                    </div>
                  );
                })}
              </div>
            </div>

            <p className="text-slate-700 mt-6 leading-relaxed">
              {grade.tone === "danger"
                ? "Your agency has significant FOIA compliance exposure. Manual processes, missed deadlines, and inconsistent redaction review create high litigation risk. The good news: every gap can be closed in 30 days with the right tooling."
                : grade.tone === "elevated"
                ? "Your agency has elevated exposure. Several gaps in your tracking, review, or audit-trail practices could trigger a costly FOIA suit. Closing two or three of them would meaningfully improve your defensibility."
                : grade.tone === "warning"
                ? "Your agency has moderate exposure. Tightening audit trails and adding consistent human-in-the-loop redaction review would substantially improve your defensibility."
                : "Your agency is in solid shape. FOIA Assist can help maintain this posture as request volume grows."}
            </p>

            {/* One-click trial activation */}
            {tempPassword ? (
              <div className="mt-8 border border-emerald-200 bg-emerald-50 p-6" data-testid="trial-created-block">
                <div className="flex items-center gap-2 mb-2">
                  <Check className="w-4 h-4 text-emerald-700" />
                  <div className="font-mono text-xs uppercase tracking-widest text-emerald-800">Trial workspace activated</div>
                </div>
                <h3 className="font-serif text-2xl mb-3">You&apos;re signed in. Welcome aboard.</h3>
                <p className="text-sm text-emerald-900 mb-4 leading-relaxed">
                  Save your temporary password so you can sign in next time. You can change it anytime from Settings.
                </p>
                <div className="flex items-center gap-2 mb-5">
                  <code className="flex-1 font-mono text-sm bg-white border border-emerald-300 px-3 py-2 break-all" data-testid="trial-temp-password">{tempPassword}</code>
                  <button onClick={copyPassword} className="fa-btn-secondary !border-emerald-300" data-testid="copy-password-button">
                    {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                    {copied ? "Copied" : "Copy"}
                  </button>
                </div>
                <button onClick={enterWorkspace} className="fa-btn-primary" data-testid="enter-workspace-button">
                  Continue to onboarding <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            ) : (
              <div className="mt-8 border border-slate-200 bg-white p-6">
                <div className="flex items-center gap-2 mb-2">
                  <Sparkles className="w-4 h-4 text-[#C5A059]" />
                  <div className="font-mono text-xs uppercase tracking-widest text-slate-600">Activate your trial in one click</div>
                </div>
                <h3 className="font-serif text-2xl mb-2">Skip the signup form. Get into your workspace now.</h3>
                <p className="text-sm text-slate-600 mb-5">
                  We&apos;ll create a 45-day trial workspace from your assessment answers and sign you in immediately. No credit card. Your risk score will be preserved on your dashboard.
                </p>
                <form onSubmit={oneClickTrial} className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <input
                    type="text"
                    name="website"
                    tabIndex={-1}
                    autoComplete="off"
                    value={signup.website}
                    onChange={(e) => setSignup({ ...signup, website: e.target.value })}
                    className="hidden"
                    aria-hidden="true"
                    data-testid="trial-honeypot"
                  />
                  <input
                    data-testid="trial-name-input"
                    required
                    placeholder="Your name"
                    value={signup.name}
                    onChange={(e) => setSignup({ ...signup, name: e.target.value })}
                    className="fa-input"
                  />
                  <input
                    data-testid="trial-agency-input"
                    required
                    placeholder="Agency / department"
                    value={signup.agency_name}
                    onChange={(e) => setSignup({ ...signup, agency_name: e.target.value })}
                    className="fa-input"
                  />
                  <input
                    data-testid="trial-email-input"
                    required
                    type="email"
                    placeholder="Work email"
                    value={signup.email}
                    onChange={(e) => setSignup({ ...signup, email: e.target.value })}
                    className="fa-input"
                  />
                  {signupErr && <div className="md:col-span-3 text-red-700 bg-red-50 border border-red-200 p-3 text-sm" data-testid="trial-signup-error">{signupErr}</div>}
                  <button
                    type="submit"
                    disabled={signupBusy}
                    className="fa-btn-primary md:col-span-3 justify-center"
                    data-testid="one-click-trial-submit"
                  >
                    {signupBusy ? "Activating workspace…" : "Activate 45-day trial"} <ArrowRight className="w-4 h-4" />
                  </button>
                </form>
                <div className="mt-4 text-xs text-slate-500">
                  Already have an account?{" "}
                  <Link to="/login" className="text-[#0B1B3D] underline decoration-[#C5A059] underline-offset-2">
                    Sign in →
                  </Link>
                </div>
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-6">
              <Link to="/register" className="fa-btn-secondary justify-center" data-testid="risk-full-signup">Prefer the full signup form →</Link>
              <Link to="/" className="fa-btn-secondary justify-center">Back to home</Link>
            </div>
          </div>
        )}
      </main>
      <PublicFooter />
    </div>
  );
}
